import { create } from '@bufbuild/protobuf'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import {
  AutoBidOrderMethod,
  type AutoBidRule,
  AutoBidRuleSchema,
  IntradayBasis,
  IntradayBidConfigSchema,
  IntradayPriceMode,
  SpotBidConfigSchema,
} from '@ebalance/proto/bffuser/v1/approval_flow_model_pb'
import {
  type Decimal,
  DecimalSchema,
} from '@ebalance/proto/google/type/decimal_pb'
import type { TFunction } from 'i18next'

/**
 * 自動入札ルール 片側 (買=不足時補填 / 売=余剰時処分) の編集用ローカル状態。
 * limitPriceYenPerKwh は orderMethod === LIMIT の時のみ意味を持つ
 * (MARKET の時は未使用だが、 モーダルで指値に戻した時の初期値として保持する)。
 */
export type AutoBidSideLocal = {
  coveragePct: number
  orderMethod: AutoBidOrderMethod
  limitPriceYenPerKwh: number
}

/** spec §4 設定項目テーブル: デフォルト値 (カバー率100・発注方法指値・価格 買20.00/売5.00) */
export const AUTO_BID_DEFAULT_COVERAGE_PCT = 100
export const AUTO_BID_DEFAULT_LIMIT_PRICE: Record<'buy' | 'sell', number> = {
  buy: 20.0,
  sell: 5.0,
}

export function defaultAutoBidSide(side: 'buy' | 'sell'): AutoBidSideLocal {
  return {
    coveragePct: AUTO_BID_DEFAULT_COVERAGE_PCT,
    orderMethod: AutoBidOrderMethod.LIMIT,
    limitPriceYenPerKwh: AUTO_BID_DEFAULT_LIMIT_PRICE[side],
  }
}

/**
 * proto AutoBidRule (undefined = 未設定) → ローカル編集状態。未設定は
 * デフォルト値で開始する (AC-039/045)。 spot 以外の process_config
 * (intraday、または未設定) はスポット欄をデフォルト値で開始する (=
 * 時間前セクションが自身の intraday config を別途読む、AC-12)。
 */
export function autoBidSideFromRule(
  rule: AutoBidRule | undefined,
  side: 'buy' | 'sell',
): AutoBidSideLocal {
  if (rule === undefined || rule.processConfig.case !== 'spot') {
    return defaultAutoBidSide(side)
  }
  const spot = rule.processConfig.value
  return {
    coveragePct: rule.coveragePct,
    orderMethod: spot.orderMethod,
    limitPriceYenPerKwh:
      spot.limitPriceYenPerKwh !== undefined
        ? Number(spot.limitPriceYenPerKwh.value)
        : AUTO_BID_DEFAULT_LIMIT_PRICE[side],
  }
}

/**
 * 時間前入札の自動入札ルール 片側の編集用ローカル状態 (design AC-02〜AC-04)。
 * basis は priceMode === DETERMINE の時のみ、 fixedPriceYenPerKwh は
 * basis === FIXED の時のみ、 counterRefPriceYenPerKwh / bidPriceYenPerKwh は
 * priceMode === SNAP_UP の時のみ意味を持つ (モーダルで条件を切り替えた時の
 * 初期値として非表示中も保持する)。
 */
export type AutoBidIntradaySideLocal = {
  coveragePct: number
  priceMode: IntradayPriceMode
  basis: IntradayBasis
  fixedPriceYenPerKwh: number
  adjustmentYenPerKwh: number
  counterRefPriceYenPerKwh: number
  bidPriceYenPerKwh: number
}

/** spec §4「時間前入札特有」設定項目テーブルのデフォルト値。 */
export const AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT = 0
export const AUTO_BID_INTRADAY_DEFAULT_FIXED_PRICE: Record<
  'buy' | 'sell',
  number
> = {
  buy: 20.0,
  sell: 5.0,
}
export const AUTO_BID_INTRADAY_DEFAULT_COUNTER_REF_PRICE: Record<
  'buy' | 'sell',
  number
> = {
  buy: 15.0,
  sell: 8.0,
}
export const AUTO_BID_INTRADAY_DEFAULT_BID_PRICE: Record<
  'buy' | 'sell',
  number
> = {
  buy: 15.0,
  sell: 8.0,
}

export function defaultAutoBidIntradaySide(
  side: 'buy' | 'sell',
): AutoBidIntradaySideLocal {
  return {
    coveragePct: AUTO_BID_DEFAULT_COVERAGE_PCT,
    priceMode: IntradayPriceMode.DETERMINE,
    basis: IntradayBasis.FIXED,
    fixedPriceYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_FIXED_PRICE[side],
    adjustmentYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT,
    counterRefPriceYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_COUNTER_REF_PRICE[side],
    bidPriceYenPerKwh: AUTO_BID_INTRADAY_DEFAULT_BID_PRICE[side],
  }
}

/**
 * proto AutoBidRule (undefined = 未設定) → 時間前入札ローカル編集状態。
 * spot 以外の process_config (未設定含む) はデフォルト値で開始する。
 */
export function autoBidIntradaySideFromRule(
  rule: AutoBidRule | undefined,
  side: 'buy' | 'sell',
): AutoBidIntradaySideLocal {
  if (rule === undefined || rule.processConfig.case !== 'intraday') {
    return defaultAutoBidIntradaySide(side)
  }
  const intraday = rule.processConfig.value
  return {
    coveragePct: rule.coveragePct,
    priceMode: intraday.priceMode,
    basis: intraday.basis ?? IntradayBasis.FIXED,
    fixedPriceYenPerKwh:
      intraday.fixedPriceYenPerKwh !== undefined
        ? Number(intraday.fixedPriceYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_FIXED_PRICE[side],
    adjustmentYenPerKwh:
      intraday.adjustmentYenPerKwh !== undefined
        ? Number(intraday.adjustmentYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_ADJUSTMENT,
    counterRefPriceYenPerKwh:
      intraday.counterRefPriceYenPerKwh !== undefined
        ? Number(intraday.counterRefPriceYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_COUNTER_REF_PRICE[side],
    bidPriceYenPerKwh:
      intraday.bidPriceYenPerKwh !== undefined
        ? Number(intraday.bidPriceYenPerKwh.value)
        : AUTO_BID_INTRADAY_DEFAULT_BID_PRICE[side],
  }
}

/**
 * 価格・調整の許容範囲 (spec §4 バリデーション表)。
 *
 * backend の実装 SoT と同値に保つこと:
 *   - go/internal/backend/domain/entity/autobid/spot_bid_config.go
 *     (minLimitPriceYenPerKwh / maxLimitPriceYenPerKwh)
 *   - go/internal/backend/domain/entity/autobid/intraday_bid_config.go
 *     (min/maxIntradayPriceYenPerKwh、 min/maxAdjustmentYenPerKwh)
 *
 * frontend 側の検証は UX 目的 (入力欄直下へインライン表示する: design/CLAUDE.md
 * 「入力エラーはインライン、 toast にしない」) であって業務制約の SoT ではない。
 */
export const AUTO_BID_MIN_PRICE = 0.01
export const AUTO_BID_MAX_PRICE = 999
export const AUTO_BID_MIN_ADJUSTMENT = -999
export const AUTO_BID_MAX_ADJUSTMENT = 999

/**
 * 範囲エラーの種別。 models は文言を持たず、 文言 key への写像は
 * `constants/autoBidValidationMessageKey.ts` の `AUTO_BID_VALIDATION_MESSAGE_KEY` が持つ。
 */
export type AutoBidValidationError = 'price' | 'adjustment'

/**
 * 価格 (指値 / 固定価格 / 対向参照価格 / 入札価格) の範囲検証。
 *
 * step 0.01 は検証しない: `decimal()` が送信時に `toFixed(2)` で丸めるため
 * 刻み違反が backend へ到達しない。
 */
export function validateAutoBidPrice(
  v: number,
): AutoBidValidationError | undefined {
  return Number.isNaN(v) || v < AUTO_BID_MIN_PRICE || v > AUTO_BID_MAX_PRICE
    ? 'price'
    : undefined
}

/** 基準からの調整 の範囲検証。 step 0.01 を検証しない理由は上と同じ。 */
export function validateAutoBidAdjustment(
  v: number,
): AutoBidValidationError | undefined {
  return Number.isNaN(v) ||
    v < AUTO_BID_MIN_ADJUSTMENT ||
    v > AUTO_BID_MAX_ADJUSTMENT
    ? 'adjustment'
    : undefined
}

/**
 * スポット入札 片側の範囲検証。 指値価格は orderMethod === LIMIT の時のみ
 * 送信される (`autoBidSpotSideToProto`) ため、 成行では検証しない。
 */
export function validateAutoBidSide(
  side: AutoBidSideLocal,
): AutoBidValidationError | undefined {
  return side.orderMethod === AutoBidOrderMethod.LIMIT
    ? validateAutoBidPrice(side.limitPriceYenPerKwh)
    : undefined
}

/** 時間前入札 片側の項目別エラー (undefined = 当該項目はエラーなし)。 */
export type AutoBidIntradayFieldErrors = {
  fixedPrice: AutoBidValidationError | undefined
  counterRefPrice: AutoBidValidationError | undefined
  bidPrice: AutoBidValidationError | undefined
  adjustment: AutoBidValidationError | undefined
}

/**
 * 時間前入札 片側の範囲検証。 priceMode / basis により送信される項目が
 * 切り替わる (`autoBidIntradaySideToProto`) ため、 送信されない項目
 * (= モーダル上でも非表示) は検証しない。 調整は常時送信される。
 */
export function validateAutoBidIntradaySide(
  side: AutoBidIntradaySideLocal,
): AutoBidIntradayFieldErrors {
  const isDetermine = side.priceMode === IntradayPriceMode.DETERMINE
  const isFixed = side.basis === IntradayBasis.FIXED
  return {
    fixedPrice:
      isDetermine && isFixed
        ? validateAutoBidPrice(side.fixedPriceYenPerKwh)
        : undefined,
    counterRefPrice: !isDetermine
      ? validateAutoBidPrice(side.counterRefPriceYenPerKwh)
      : undefined,
    bidPrice: !isDetermine
      ? validateAutoBidPrice(side.bidPriceYenPerKwh)
      : undefined,
    adjustment: validateAutoBidAdjustment(side.adjustmentYenPerKwh),
  }
}

/**
 * 時間前入札 片側の最初の範囲エラー (undefined = エラーなし)。 保存ボタンの
 * 無効化と、 無効理由の tooltip 文言に使う。
 */
export function firstAutoBidIntradayError(
  side: AutoBidIntradaySideLocal,
): AutoBidValidationError | undefined {
  return Object.values(validateAutoBidIntradaySide(side)).find(
    (e) => e !== undefined,
  )
}

export type AutoBidRulesByArea = ReadonlyMap<
  AreaCode,
  { buy?: AutoBidRule; sell?: AutoBidRule }
>

/** AutoBidRule[] (proto、 bid_side で買/売混在) → エリア別 { buy, sell } map。 */
export function groupAutoBidRulesByArea(
  rules: readonly AutoBidRule[],
): AutoBidRulesByArea {
  const map = new Map<AreaCode, { buy?: AutoBidRule; sell?: AutoBidRule }>()
  for (const r of rules) {
    const entry = map.get(r.areaCode) ?? {}
    if (r.bidSide === Side.BUY) {
      map.set(r.areaCode, { ...entry, buy: r })
    } else if (r.bidSide === Side.SELL) {
      map.set(r.areaCode, { ...entry, sell: r })
    }
  }
  return map
}

/**
 * 承認モード=自動 のエリアで、 当該エリアの自動入札ルール（買い・売り）が
 * 一度も明示保存されていないか (AC-01/AC-056b)。 backend GetApprovalFlowScreen
 * は DB に行がある分だけ auto_bid_rules を返す (proto に含まれない = 未保存) ため、
 * groupAutoBidRulesByArea の結果に対して buy/sell いずれかが undefined であれば
 * true。 買い・売り両方の存在を要求する (spec AC-056a: SaveAutoBidRule は
 * buy/sell をペアで required、 片方のみの保存は成立しない)。
 */
export function isAutoBidRuleUnsaved(
  rulesByArea: AutoBidRulesByArea,
  areaCode: AreaCode,
): boolean {
  const entry = rulesByArea.get(areaCode)
  return entry?.buy === undefined || entry.sell === undefined
}

/** 円/kWh の表示整形 (小数第2位固定)。 */
function formatPrice(v: number): string {
  return v.toFixed(2)
}

/**
 * 時間前入札の要約で使う価格整形。 **末尾の 0 を落とす** (`20.00` ではなく `20`)。
 *
 * spot 側の [[formatPrice]] (`toFixed(2)`) と違うのは、 AC-APF-047 の書式が
 * prototype の出荷済み実装 (`js/autobid-rule.js` の `fmtNum`:
 * `(Math.round(v * 100) / 100).toString()`) から起こされているため。 spec の例も
 * `固定20` / `+0.5` と末尾 0 が無い形で書かれている。 2 桁固定にすると
 * spec・prototype の両方と表示が食い違う。
 */
function formatIntradayNumber(v: number): string {
  return (Math.round(v * 100) / 100).toString()
}

/**
 * 「基準からの調整」の後置表示。 **0 は空文字** (AC-APF-047:「0 の時は表示せず、
 * 0 以外は符号付きで後置する」)。 prototype の `fmtSigned` と同値。
 */
function formatIntradayAdjustment(v: number): string {
  if (v === 0 || Number.isNaN(v)) return ''
  return `${v > 0 ? '+' : ''}${formatIntradayNumber(v)}`
}

/**
 * 基準「最良気配」の表示名。 買いは最良売り気配・売りは最良買い気配を参照するため、
 * 英語は売買で訳し分ける (approval-flow.md 付録「売買を区別しない訳は設けない」)。
 * 日本語はどちらも「最良気配」。
 */
export function intradayBestQuoteLabel(
  t: TFunction,
  sideKind: 'buy' | 'sell',
): string {
  return sideKind === 'buy'
    ? t('page:adminApprovalFlow.autoBid.modal.intraday.basisBestQuoteBuy')
    : t('page:adminApprovalFlow.autoBid.modal.intraday.basisBestQuoteSell')
}

/** 基準 (最良気配 / 直近約定 / 固定) の表示名。 modal と同じ i18n キーを使う。 */
function intradayBasisLabel(
  t: TFunction,
  basis: IntradayBasis,
  sideKind: 'buy' | 'sell',
): string {
  switch (basis) {
    case IntradayBasis.BEST_QUOTE:
      return intradayBestQuoteLabel(t, sideKind)
    case IntradayBasis.LATEST_TRADE:
      return t('page:adminApprovalFlow.autoBid.modal.intraday.basisLatestTrade')
    default:
      return t('page:adminApprovalFlow.autoBid.modal.intraday.basisFixed')
  }
}

/**
 * 時間前入札のセル / 一括要約テキスト (AC-APF-047 の `{発注内容}`)。
 *
 * | 条件 | 書式 |
 * |---|---|
 * | 価格を決める・基準 = 固定 | `固定{固定価格}{調整}` |
 * | 価格を決める・基準 ≠ 固定 | `{基準}{調整}` |
 * | 食いつき | `食いつき 対向{≤\|≥}{対向参照価格}→入札{入札価格}{調整}` |
 *
 * 比較記号は **買い = `≤` / 売り = `≥`** (対向 = 買い側から見た売り気配 /
 * 売り側から見た買い気配)。
 *
 * **spot 形 (`指値` / `成行`) で描いてはならない** — 価格モード・基準・対向参照価格は
 * 時間前にしか無い項目で、 spot 形に落とすと保存済みのルールが既定値に化けて見える
 * (AC-APF-047 の注記、 鉄則2 / AC-APF-056a)。
 */
export function autoBidIntradaySideSummaryText(
  t: TFunction,
  side: AutoBidIntradaySideLocal,
  sideKind: 'buy' | 'sell',
  fee: AutoBidFeeAutoConsider,
): string {
  const adjustment = formatIntradayAdjustment(side.adjustmentYenPerKwh)
  // 手数料自動考慮 ON の保存値は希望価格なので `→入札` を `→希望` にする (AC-APF-047)。
  const snapUpKey = isAutoBidFeeAutoConsiderOn(fee)
    ? 'page:adminApprovalFlow.autoBid.detailIntradaySnapUpDesired'
    : 'page:adminApprovalFlow.autoBid.detailIntradaySnapUp'
  const detail =
    side.priceMode === IntradayPriceMode.SNAP_UP
      ? t(snapUpKey, {
          comparator: sideKind === 'buy' ? '≤' : '≥',
          counterRef: formatIntradayNumber(side.counterRefPriceYenPerKwh),
          bid: formatIntradayNumber(side.bidPriceYenPerKwh),
          adjustment,
        })
      : side.basis === IntradayBasis.FIXED
        ? t('page:adminApprovalFlow.autoBid.detailIntradayFixed', {
            price: formatIntradayNumber(side.fixedPriceYenPerKwh),
            adjustment,
          })
        : t('page:adminApprovalFlow.autoBid.detailIntradayBasis', {
            basis: intradayBasisLabel(t, side.basis, sideKind),
            adjustment,
          })
  return t('page:adminApprovalFlow.autoBid.summaryFormat', {
    coverage: side.coveragePct,
    detail,
  })
}

/**
 * 時間前入札のルールが「明示保存済み」か。 [[isAutoBidRuleUnsaved]] は buy/sell の
 * 有無しか見ないので、 **process_config が intraday であること**まで要求する。
 *
 * spot のルールが混ざった状態で通すと、 [[autoBidIntradaySideFromRule]] が
 * 既定値へ落とすため「保存済みなのに既定値」 という表示になり、
 * 未保存を設定済みに見せる (鉄則2)。 呼び出し元は業務処理ごとに
 * `autoBidRules` を絞っている (`AdminApprovalFlow/index.tsx`) が、
 * 表示側でも取り違えを検出できるようにする。
 */
export function isAutoBidIntradayRuleSaved(
  rulesByArea: AutoBidRulesByArea,
  areaCode: AreaCode,
): boolean {
  const entry = rulesByArea.get(areaCode)
  return (
    entry?.buy?.processConfig.case === 'intraday' &&
    entry.sell?.processConfig.case === 'intraday'
  )
}

/** セル要約テキスト (AC-01 書式: `カバー率{N}% / {発注内容}`)。 */
export function autoBidSideSummaryText(
  t: TFunction,
  side: AutoBidSideLocal,
): string {
  const detail =
    side.orderMethod === AutoBidOrderMethod.MARKET
      ? t('page:adminApprovalFlow.autoBid.detailMarket')
      : t('page:adminApprovalFlow.autoBid.detailLimit', {
          price: formatPrice(side.limitPriceYenPerKwh),
        })
  return t('page:adminApprovalFlow.autoBid.summaryFormat', {
    coverage: side.coveragePct,
    detail,
  })
}

/** bid-limits の価格レンジ (undefined = 制限なし)。 */
export type AutoBidPriceRange = {
  min: number | undefined
  max: number | undefined
}

export type AutoBidEffective = {
  /** 発注に使われる想定の入札価格。 成行かつ成行許可の場合は undefined (指値変換なし)。 */
  effectivePrice: number | undefined
  /** 表示に⚠を付けるべきか (= 上下限で clamp、 JEPX 値域外、 または成行不可 → 指値変換)。 */
  warning: boolean
  /** 成行不可のため指値に変換されたか (AC-043/047)。 */
  marketConverted: boolean
  /** 入札価格が JEPX の値域外で、 発火時に発注されないか (AC-APF-043 / 053)。 */
  outsideJepx: boolean
}

/**
 * 価格の実効値 (上下限を適用した後の入札価格、 AC-APF-043)。
 * `outsideJepx` は clamp 後も JEPX の値域を外れ、 発火時にスキップされる価格。
 */
export type AutoBidPriceEffective =
  | { kind: 'price'; value: number; clamped: boolean }
  | { kind: 'outsideJepx'; value: number }

/** JEPX 制度の入札価格の値域 (円/kWh、 AC-APF-043 / 053)。 */
const JEPX_MIN_PRICE_SEN = 1
const JEPX_MAX_PRICE_SEN = 99900

/**
 * 円/kWh を 0.01 円単位の整数へ丸める。 backend は decimal で計算するため、
 * 浮動小数の加算誤差 (998.99 + 0.01 等) で境界の判定がずれないよう整数で比べる。
 */
function toSen(yen: number): number {
  return Math.round(yen * 100)
}

/**
 * desired (手数料と上下限を適用する前の価格) から価格の実効値を求める。
 * 上下限で clamp した後に JEPX の値域を判定する (spec §3「手数料自動考慮との関係」)。
 * 表示専用で、 発火時の値は backend `IntradayBidConfig.DesiredPrice` と
 * 実行層の clamp が決める。
 */
export function computeAutoBidPriceEffective(
  desiredYenPerKwh: number,
  range: AutoBidPriceRange,
): AutoBidPriceEffective {
  const desired = toSen(desiredYenPerKwh)
  const min = range.min !== undefined ? toSen(range.min) : undefined
  const max = range.max !== undefined ? toSen(range.max) : undefined
  let sen = desired
  if (min !== undefined && sen < min) sen = min
  if (max !== undefined && sen > max) sen = max
  const value = sen / 100
  if (sen < JEPX_MIN_PRICE_SEN || sen > JEPX_MAX_PRICE_SEN) {
    return { kind: 'outsideJepx', value }
  }
  return { kind: 'price', value, clamped: sen !== desired }
}

/**
 * 手数料自動考慮 (`bid-limits.md` AC-BDL-015〜020) の、 ルールの価格の扱い。
 * `unavailable` は ON だが手数料単価を使えない間 (取得中・取得失敗・使えない単価)。
 * 欄名は ON のまま、 価格の実効値は出さない (発火は代替の単価で加減算するため、
 * 未調整の値を見せると入札価格とずれる。 AC-APF-068)。
 */
export type AutoBidFeeAutoConsider =
  | { kind: 'off' }
  | { kind: 'on'; feeYenPerKwh: number }
  | { kind: 'unavailable' }

export const AUTO_BID_FEE_OFF: AutoBidFeeAutoConsider = { kind: 'off' }

/** 欄名・サマリを希望価格の呼び方にするか (AC-APF-041 / 042 / 047)。 */
export function isAutoBidFeeAutoConsiderOn(
  fee: AutoBidFeeAutoConsider,
): boolean {
  return fee.kind !== 'off'
}

/**
 * 希望価格に手数料単価を加減算した入札価格 (売り + / 買い −。 backend
 * `ComputeFeeAdjustedOrderPrice` と同じ符号)。 0.01 円単位の整数で足す。
 *
 * rule (業務計算は backend) から外れて frontend に置く: 上下限の clamp と同じ表示専用の
 * プレビューで、 入札価格は backend の発火が決める。 入札画面の preview RPC は「範囲外は
 * 作成不可」 で、 本画面の「範囲外は clamp して ⚠」 と意味が違うので使わない。 単価の
 * 読み元が組織ごと・DB 由来になった時か、 この値が保存の可否を決めるようになった時は
 * backend に寄せる (#11191)。
 */
export function autoBidFeeAdjustedPrice(
  desiredYenPerKwh: number,
  sideKind: 'buy' | 'sell',
  feeYenPerKwh: number,
): number {
  const fee = toSen(feeYenPerKwh)
  return (toSen(desiredYenPerKwh) + (sideKind === 'sell' ? fee : -fee)) / 100
}

/**
 * 手数料の加減算 → 上下限の clamp → JEPX 値域の判定 (spec §3「手数料自動考慮との
 * 関係」)。 clamp の判定は加減算後の値と比べるので、 希望価格が範囲内でも加減算後に
 * 範囲外なら clamped になる (AC-APF-043)。 単価を使えない間は undefined。
 */
function computeAutoBidFeeAwarePriceEffective(
  desiredYenPerKwh: number,
  sideKind: 'buy' | 'sell',
  range: AutoBidPriceRange,
  fee: AutoBidFeeAutoConsider,
): AutoBidPriceEffective | undefined {
  switch (fee.kind) {
    case 'off':
      return computeAutoBidPriceEffective(desiredYenPerKwh, range)
    case 'on':
      return computeAutoBidPriceEffective(
        autoBidFeeAdjustedPrice(desiredYenPerKwh, sideKind, fee.feeYenPerKwh),
        range,
      )
    case 'unavailable':
      return undefined
  }
}

/**
 * 時間前ルールの desired (価格を直接入力する欄 + 基準からの調整)。
 * 基準 = 固定なら固定価格 + 調整、 食いつきなら入札価格 + 調整 (backend
 * `IntradayBidConfig.DesiredPrice` と同じ)。 動的基準 (最良気配 / 直近約定) は
 * 設定時に価格が定まらないので undefined (AC-APF-043)。 入力途中で数値に
 * ならない時も undefined を返す。 各欄は保存時 ([[decimal]]) と同じく 0.01 単位に
 * 丸めてから足す (発火時の価格は保存値から決まるため)。
 */
export function autoBidIntradayDesiredPrice(
  side: AutoBidIntradaySideLocal,
): number | undefined {
  let base: number
  if (side.priceMode === IntradayPriceMode.SNAP_UP) {
    base = side.bidPriceYenPerKwh
  } else if (side.basis === IntradayBasis.FIXED) {
    base = side.fixedPriceYenPerKwh
  } else {
    return undefined
  }
  const desired = asSaved(base) + asSaved(side.adjustmentYenPerKwh)
  return Number.isNaN(desired) ? undefined : desired
}

function effectiveFromPrice(price: AutoBidPriceEffective): AutoBidEffective {
  return price.kind === 'outsideJepx'
    ? {
        effectivePrice: price.value,
        warning: true,
        marketConverted: false,
        outsideJepx: true,
      }
    : {
        effectivePrice: price.value,
        warning: price.clamped,
        marketConverted: false,
        outsideJepx: false,
      }
}

/**
 * ルールデータ自体は書き換えず (spec AC-043/047)、 bid-limits の実効値を
 * 計算する表示専用ロジック (サーバ確定値の再計算ではない UX プレビュー。
 * 実際の発注時 clamp は
 * 実行層 usecase の責務、#2511)。
 */
export function computeAutoBidEffective(
  side: AutoBidSideLocal,
  sideKind: 'buy' | 'sell',
  priceRange: AutoBidPriceRange,
  marketOrderAllowed: boolean,
  fee: AutoBidFeeAutoConsider,
): AutoBidEffective | undefined {
  if (side.orderMethod === AutoBidOrderMethod.MARKET) {
    if (marketOrderAllowed) {
      return {
        effectivePrice: undefined,
        warning: false,
        marketConverted: false,
        outsideJepx: false,
      }
    }
    // 成行不可 → 買=実効上限 / 売=実効下限 で指値変換 (spec §4 発注方法備考)。
    // 上下限が空欄 (制限なし) の側は JEPX の値域端になる (backend
    // `bidlimit.Matrix.EffectivePriceRange` と揃える)。
    const converted =
      sideKind === 'buy'
        ? (priceRange.max ?? AUTO_BID_MAX_PRICE)
        : (priceRange.min ?? AUTO_BID_MIN_PRICE)
    return {
      effectivePrice: converted,
      warning: true,
      marketConverted: true,
      outsideJepx: false,
    }
  }
  const price = computeAutoBidFeeAwarePriceEffective(
    asSaved(side.limitPriceYenPerKwh),
    sideKind,
    priceRange,
    fee,
  )
  return price === undefined ? undefined : effectiveFromPrice(price)
}

/**
 * 時間前ルール片側の実効値。 価格の実効値を出せない (動的基準) 時は undefined で、
 * ⚠ の対象にもしない (AC-APF-047 の注記)。
 */
export function computeAutoBidIntradayEffective(
  side: AutoBidIntradaySideLocal,
  sideKind: 'buy' | 'sell',
  priceRange: AutoBidPriceRange,
  fee: AutoBidFeeAutoConsider,
): AutoBidEffective | undefined {
  const desired = autoBidIntradayDesiredPrice(side)
  if (desired === undefined) return undefined
  const price = computeAutoBidFeeAwarePriceEffective(
    desired,
    sideKind,
    priceRange,
    fee,
  )
  return price === undefined ? undefined : effectiveFromPrice(price)
}

/** 保存時に送る値 ([[decimal]] の丸め) を number で返す。 */
function asSaved(v: number): number {
  return Number(v.toFixed(2))
}

function decimal(v: number): Decimal {
  return create(DecimalSchema, { value: v.toFixed(2) })
}

export function autoBidSpotSideToProto(
  area: AreaCode,
  bidSide: Side,
  side: AutoBidSideLocal,
): AutoBidRule {
  return create(AutoBidRuleSchema, {
    areaCode: area,
    bidSide,
    coveragePct: side.coveragePct,
    processConfig: {
      case: 'spot',
      value: create(SpotBidConfigSchema, {
        orderMethod: side.orderMethod,
        limitPriceYenPerKwh:
          side.orderMethod === AutoBidOrderMethod.LIMIT
            ? decimal(side.limitPriceYenPerKwh)
            : undefined,
      }),
    },
  })
}

export function autoBidIntradaySideToProto(
  area: AreaCode,
  bidSide: Side,
  side: AutoBidIntradaySideLocal,
): AutoBidRule {
  const isDetermine = side.priceMode === IntradayPriceMode.DETERMINE
  const isFixed = side.basis === IntradayBasis.FIXED
  return create(AutoBidRuleSchema, {
    areaCode: area,
    bidSide,
    coveragePct: side.coveragePct,
    processConfig: {
      case: 'intraday',
      value: create(IntradayBidConfigSchema, {
        priceMode: side.priceMode,
        basis: isDetermine ? side.basis : undefined,
        fixedPriceYenPerKwh:
          isDetermine && isFixed
            ? decimal(side.fixedPriceYenPerKwh)
            : undefined,
        adjustmentYenPerKwh: decimal(side.adjustmentYenPerKwh),
        counterRefPriceYenPerKwh: !isDetermine
          ? decimal(side.counterRefPriceYenPerKwh)
          : undefined,
        bidPriceYenPerKwh: !isDetermine
          ? decimal(side.bidPriceYenPerKwh)
          : undefined,
      }),
    },
  })
}
